import * as CERT_STATUSES from 'Config/profile-certification-statuses';
import * as BO_ITEM_STATUSES from 'Config/profile-benefcial-owners-item-status';
import * as INVESTMENT_PROFILE_STATUS from 'Config/investment-profile-kyc-statuses';
import { PROFILE_TYPES, ACCREDITATION_PROFILE_STATUS } from 'Const/investment';
import { SM_STATUS as PROFILE_SM_STATUS } from 'Const/profile.json';
import { get } from 'Utils/common';

export default {
  profileModel: state => state.profileModel,
  profileModelLoading: state => state.profileModel.$loading,
  profileData: state => state.profileModel.$serverData || {},
  profile: (state, getters) => getters.profileData,
  profileEmail: (state, getters) => getters.profile.email,
  profileDataLoading: state => state.profileModel.$loadingData,
  profileDataLoaded: state => state.profileModel.$dataLoaded,
  profileDataPromise: state => (
    state.profileModel.$dataLoadingPromise
    || state.profileDataPromiseTemp
  ),
  profileFullName: (state, getters) => {
    const model = getters.individualInvestmentProfileId
      ? get(getters.individualInvestmentProfile, 'personal', {})
      : getters.profile;
    const firstName = model.first_name || '';
    const lastName = model.last_name || '';
    return `${firstName} ${lastName}`;
  },
  profileBalance: (state, getters) => getters.profile.balance, // sum of all user's wallet
  isProfileFillMessageClosed: state => state.isProfileFillMessageClosed,
  investmentProfileList: state => state.investmentProfileList,
  investmentProfileListLoading: state => state.investmentProfileListLoading,
  // Get last used investment profile based on last_usage_date profile property
  lastUsedInvestmentProfileId: (state, getters) => {
    if (!getters.investmentProfileList || !getters.investmentProfileList.length) return null;
    const lastProfile = getters.investmentProfileList.reduce((resultProfile, profile) => {
      if (
        resultProfile
        && Date.parse(resultProfile.last_usage_date) > (Date.parse(profile.last_usage_date) || 0)
      ) {
        return resultProfile;
      }
      // Check if current profile was used at all
      if (profile.last_usage_date) return profile;
      return null;
    });
    return (lastProfile && lastProfile.last_usage_date) ? lastProfile.id : null;
  },
  watchFarms: state => state.watchFarms,
  watchFarmsLoading: state => state.watchFarmsLoading,
  watchFarmToggleLoading: state => state.watchFarmToggleLoading,
  isPrivacyConfirmed: (state, getters) => Boolean(getters.profile.confirm_privacy),
  isAccredited: (state, getters) => Boolean(getters.profileModel.isAccredited),
  financialSituation: (state, getters) => getters.profileData.financial_situation,
  selectedInvestmentProfile: (state, getters, rootState, rootGetters) => {
    if (!getters.selectedInvestmentProfileId) return {};
    return rootGetters['investment/selectedProfile']
      || getters.investmentProfileList.find(item => (
        item.id === getters.selectedInvestmentProfileId
      ))
      || {};
  },
  isSelectedInvestmentProfileSdira: (state, getters) => (
    getters.selectedInvestmentProfile.type === PROFILE_TYPES.SDIRA
  ),
  isSelectedInvestmentProfileIndividual: (state, getters) => (
    getters.selectedInvestmentProfile.type === PROFILE_TYPES.INDIVIDUAL
  ),
  isSelectedInvestmentProfileAccredited: (state, getters) => (
    getters.selectedInvestmentProfile.accreditation_status === ACCREDITATION_PROFILE_STATUS.APPROVED
  ),
  isSelectedInvestmentProfileImportantAccredited: (state, getters) => (
    getters.selectedInvestmentProfile.accreditation_permanent_approved
  ),
  isSelectedInvestmentProfileExpiredSoon: (state, getters) => (
    getters.selectedInvestmentProfile.accreditation_status === ACCREDITATION_PROFILE_STATUS.EXPIRED_SOON
  ),
  isExistInvestmentProfileSDIRA: (state, getters) => (
    getters.investmentProfileList.some(item => item.type === PROFILE_TYPES.SDIRA)
  ),
  individualInvestmentProfile: (state, getters) => (
    getters.investmentProfileList.find(item => item.type === PROFILE_TYPES.INDIVIDUAL)
  ),
  // Individual profile id can be id of created individual profile, or null as default value
  individualInvestmentProfileId: (state, getters) => (
    (getters.individualInvestmentProfile && getters.individualInvestmentProfile.id) || null
  ),
  individualInvestmentProfileMarketStatus: (state, getters) => (
    getters.individualInvestmentProfile?.sm_status
  ),
  isMarketAbleToParticipate: (state, getters) => (
    // Market is ineligible for accounts from Washington
    getters.individualInvestmentProfile?.personal?.state !== 'WA'
  ),
  isAllowedMarketOnboarding: (state, getters) => (
    getters.individualInvestmentProfile?.status === INVESTMENT_PROFILE_STATUS.VERIFIED
    && Boolean(getters.individualInvestmentProfile?.accreditation_status)
  ),
  isIndividualInvestmentMarketOnboardingApproved: (state, getters) => (
    Boolean(getters.individualInvestmentProfileMarketStatus === PROFILE_SM_STATUS.APPROVED)
  ),
  // Get profile id from investment first
  // then from state
  // if there is no selected profile, make individual as default
  selectedInvestmentProfileId: (state, getters, rootState, rootGetters) => (
    rootGetters['investment/selectedProfileId']
    || state.selectedInvestmentProfileId
  ),
  investmentProfileAccreditationIssueList: state => state.investmentProfileAccreditationIssueList,
  status: (state, getters) => getters.selectedInvestmentProfile.status,
  KYCStatus: (state, getters) => getters.status,
  isStatusNew: (state, getters) => !getters.status,
  isStatusUnverified: (state, getters) => getters.status === INVESTMENT_PROFILE_STATUS.UNVERIFIED,
  isStatusSuspended: (state, getters) => getters.status === INVESTMENT_PROFILE_STATUS.SUSPENDED,
  isStatusRetry: (state, getters) => getters.status === INVESTMENT_PROFILE_STATUS.RETRY,
  isStatusRetrySent: (state, getters) => getters.status === INVESTMENT_PROFILE_STATUS.RETRY_SENT,
  isStatusDocument: (state, getters) => getters.status === INVESTMENT_PROFILE_STATUS.DOCUMENT,
  isStatusDocumentSent: (state, getters) => getters.status === INVESTMENT_PROFILE_STATUS.DOCUMENT_SENT,
  isStatusVerified: (state, getters) => getters.status === INVESTMENT_PROFILE_STATUS.VERIFIED,
  isIdVerificationSent: (state, getters) => Boolean(getters.status),
  isIdVerificationFormActionRequired: (state, getters) => Boolean(
    getters.isStatusNew || getters.isStatusRetry || getters.isStatusDocument,
  ),
  walletData: state => state.walletData,
  walletDataLoading: state => state.walletDataLoading,
  walletBalance: state => (
    (state.walletBalance && state.walletBalance.id) ? {
      ...state.walletBalance, type: 'balance', name: 'Balance',
    } : {}
  ),
  walletBalanceValue: (state, getters) => (
    (getters.walletBalance && getters.walletBalance.value) || 0
  ),
  walletBalanceValuePending: (state, getters) => (
    (getters.walletBalance && getters.walletBalance.pending) || 0
  ),
  walletBalancePendingInvestments: (state, getters) => (
    (getters.walletBalance && getters.walletBalance.pending_investments) || 0
  ),
  walletBalanceTotal: (state, getters) => (
    getters.walletBalanceValue + getters.walletBalanceValuePending - getters.walletBalancePendingInvestments
  ),
  walletBanks: state => state.walletBanks || [],
  walletCanLinkBankAccount: state => state.walletCanLinkBankAccount,
  walletCanWithdraw: state => state.walletCanWithdraw,
  fundingSource: (state, getters) => (
    getters.walletBalance.id
      ? getters.walletBanks.concat(getters.walletBalance)
      : getters.walletBanks),
  addFundingSourceLoading: state => state.addFundingSourceLoading,
  isLinkedBankAccount: (state, getters) => Boolean(getters.walletBanks.length),
  walletTransactionsData: state => state.walletTransactionsData,
  walletTransactionsFutureData: (state, getters) => (
    getters.walletBalance.pending_transactions || []
  ),
  // All transaction and injected pending investment (future) transactions
  // but only newer than last real transaction
  walletTransactionsAllData: (state, getters) => {
    // get transactions lists
    const {
      walletTransactionsFutureData,
      walletTransactionsData,
      isWalletTransactionsLoadedAll,
    } = getters;
    // if real transactions empty, return future transactions
    // if future transactions empty too, empty array is acceptable too
    if (!walletTransactionsData.length) return walletTransactionsFutureData;
    // get last real transaction
    const lastRealTransaction = walletTransactionsData[walletTransactionsData.length - 1];
    // add all future transaction
    let futureTransactionsToInject = walletTransactionsFutureData;
    // but if loaded not all real transactions
    if (!isWalletTransactionsLoadedAll) {
      // then add only newer future transaction than last loaded real transaction
      futureTransactionsToInject = walletTransactionsFutureData.filter(item => (
        new Date(item.created) > new Date(lastRealTransaction.created)
      ));
    }
    // concat transactions together
    const all = walletTransactionsData.concat(futureTransactionsToInject);
    // sort by created time
    all.sort((a, b) => new Date(b.created) - new Date(a.created));
    return all;
  },
  walletTransactionsCount: state => state.walletTransactionsCount,
  walletTransactionsLoadedCount: (state, getters) => getters.walletTransactionsData.length,
  walletTransactionsLoading: state => state.walletTransactionsLoading,
  isWalletTransactionsLoadedAll: (state, getters) => (
    getters.walletTransactionsLoadedCount >= getters.walletTransactionsCount
  ),
  certificationStatus: (state, getters) => getters.selectedInvestmentProfile.certification_status,
  // Should be sent at least one form for KYC to BO can be send
  isBeneficialOwnersRequired: (state, getters) => (
    getters.isIdVerificationSent
    && getters.selectedInvestmentProfile.type === PROFILE_TYPES.ENTITY
  ),
  isCertificationPassed: (state, getters) => (
    getters.certificationStatus === CERT_STATUSES.CERTIFIED
  ),
  isCertificationRequired: (state, getters) => (
    getters.isBeneficialOwnersRequired
    && !getters.isCertificationPassed
  ),
  isReadyToCertify: (state, getters) => Boolean( // Form ready to certify when:
    // beneficial owners data loaded
    getters.beneficialOwnersData.length
    // and there aren't any pending items
    && !getters.beneficialOwnersPendingItems.length
    // and there aren't any action required items
    && !getters.beneficialOwnersActionRequiredItems.length,
  ),
  isBeneficialOwnersStatusNew: (state, getters) => (
    getters.isCertificationRequired && !getters.beneficialOwnersData.length
  ),
  beneficialOwnersData: state => state.beneficialOwnersData,
  beneficialOwnersLoading: state => state.beneficialOwnersLoading,
  beneficialOwnersLoaded: state => state.beneficialOwnersLoaded,
  beneficialOwnersActionRequiredItems: state => state.beneficialOwnersData.filter(item => (
    ![BO_ITEM_STATUSES.DOCUMENT_PENDING, BO_ITEM_STATUSES.VERIFIED].includes(item.status)
  )),
  beneficialOwnersPendingItems: state => state.beneficialOwnersData.filter(item => (
    [BO_ITEM_STATUSES.DOCUMENT_PENDING].includes(item.status)
  )),
  beneficialOwnersNotReadyItems: (state, getters) => (
    getters.beneficialOwnersActionRequiredItems.concat(getters.beneficialOwnersPendingItems)
  ),
  isBeneficialOwnersFormActionRequired: (state, getters) => (
    Boolean(
      getters.beneficialOwnersActionRequiredItems.length
      || (!getters.beneficialOwnersPendingItems.length && getters.isCertificationRequired),
    )
  ),
  isIdVerificationComplete: (state, getters) => (
    getters.isStatusVerified && !getters.isCertificationRequired
  ),
  businessName: (state, getters) => get(getters.selectedInvestmentProfile, 'ownership.entity_name', ''),
  certificationDate: (state, getters) => getters.selectedInvestmentProfile.certification_date || 0,
  isSomeInvestmentProfileVerified: state => (
    state.investmentProfileList.some(profile => (
      profile.status === INVESTMENT_PROFILE_STATUS.VERIFIED
      && (profile.type !== PROFILE_TYPES.ENTITY || profile.certification_status === CERT_STATUSES.CERTIFIED)
    ))
  ),
  // Is some investment profile has approved status of accreditation
  isSomeInvestmentProfileAccredited: state => (
    state.investmentProfileList.some(profile => profile.accreditation_status === ACCREDITATION_PROFILE_STATUS.APPROVED)
    || state.investmentProfileList.some(profile => profile.accreditation_status === ACCREDITATION_PROFILE_STATUS.EXPIRED_SOON)
  ),
  // Is some investment profile pass accreditation early or accredited right now
  isSomeInvestmentProfilePassAccreditation: (state, getters) => (
    getters.isSomeInvestmentProfileAccredited
    || state.investmentProfileList.some(profile => (
      [ACCREDITATION_PROFILE_STATUS.EXPIRED, ACCREDITATION_PROFILE_STATUS.NEW_INFO_ADDED]
        .includes(profile.accreditation_status)),
    )
  ),
  investmentProfileSSNList: state => state.investmentProfileSSNList,
  selectedInvestmentProfileSSN: (state, getters) => {
    const ssnObject = getters.investmentProfileSSNList
      .find(item => item.profileId === getters.selectedInvestmentProfileId);
    return ssnObject ? ssnObject.ssn : '';
  },
  selectedInvestmentProfileSSNShort: (state, getters) => (
    getters.selectedInvestmentProfileSSN ? getters.selectedInvestmentProfileSSN.substr(-4) : ''
  ),
  getInvestmentProfileSSN: (state, getters) => (profileId) => {
    if (!profileId) return '';
    const ssnObject = getters.investmentProfileSSNList.find(item => item.profileId === profileId);
    return ssnObject ? ssnObject.ssn : '';
  },
  isAllowedMarketSell: (state, getters) => (
    getters.isIndividualInvestmentMarketOnboardingApproved
  ),
  isAllowedMarketBuy: (state, getters) => (
    getters.isIndividualInvestmentMarketOnboardingApproved
    && (getters.individualInvestmentProfile?.accreditation_status === ACCREDITATION_PROFILE_STATUS.APPROVED
    || getters.individualInvestmentProfile?.accreditation_status === ACCREDITATION_PROFILE_STATUS.EXPIRED_SOON)
  ),
};
